/**
 * ContactMessageDialog — staff → contact one-off message by email, WhatsApp and/or SMS.
 * The server only sends on channels the contact consented to (see server/utils/directMessage.js);
 * this dialog shows that consent up front so nobody is surprised by a refused channel.
 *
 * Remount it per contact (give it a `key`) so the draft resets between contacts.
 */
import { useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

const CHANNELS = [
  { value: "whatsapp", label: "WhatsApp", icon: "chat" },
  { value: "email", label: "Email", icon: "email" },
  { value: "sms", label: "SMS", icon: "sms" },
];
const LABELS = Object.fromEntries(CHANNELS.map((c) => [c.value, c.label]));

// WhatsApp first: it's how the team follows up on calls. Falls back to email.
const defaultChannels = (consent) => {
  if (consent.whatsapp === true) return ["whatsapp"];
  if (consent.email === true) return ["email"];
  return ["email"];
};

function ContactMessageDialog({ open, onClose, contact, send }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const consent = contact.communicationConsent || {};
  const [channels, setChannels] = useState(() => defaultChannels(consent));
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const refused = channels.filter((c) => consent[c] !== true);

  const toggleChannels = (_, next) => {
    if (next.length > 0) setChannels(next);
  };

  const handleSend = async () => {
    if (!message.trim()) {
      setError("Write a message first.");
      return;
    }
    if (channels.includes("email") && !subject.trim()) {
      setError("Email needs a subject.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const result = await send({ subject: subject.trim(), message, channels });
      const failed = Object.entries(result.results || {}).filter(([, r]) => !r.success);
      if (failed.length === 0) {
        setSuccess(`Sent via ${channels.map((c) => LABELS[c]).join(" + ")} to ${contact.name}.`);
        setSubject("");
        setMessage("");
      } else {
        const sent = Object.entries(result.results || {})
          .filter(([, r]) => r.success)
          .map(([c]) => LABELS[c] || c);
        const reasons = failed
          .map(([c, r]) => `${LABELS[c] || c}: ${r.error || "failed"}`)
          .join("; ");
        setError(
          `${sent.length ? `Sent via ${sent.join(" + ")}, but not ` : "Not sent — "}${reasons}`
        );
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send the message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isPhone}
    >
      <DialogTitle>
        Message {contact.name}
        <MDTypography variant="caption" display="block" color="secondary">
          {contact.email || "no email"} · {contact.phone || "no phone"}
        </MDTypography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <MDBox mb={2}>
            <MDAlert color="error" dismissible onClose={() => setError("")}>
              {error}
            </MDAlert>
          </MDBox>
        )}
        {success && (
          <MDBox mb={2}>
            <MDAlert color="success" dismissible onClose={() => setSuccess("")}>
              {success}
            </MDAlert>
          </MDBox>
        )}

        <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
          Send via
        </MDTypography>
        <ToggleButtonGroup
          value={channels}
          onChange={toggleChannels}
          size="small"
          fullWidth={isPhone}
          sx={{ mb: 1 }}
        >
          {CHANNELS.map((c) => (
            <ToggleButton key={c.value} value={c.value}>
              <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>{c.icon}</Icon>
              {c.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={0.75} mb={2}>
          <MDTypography variant="caption" color="text">
            Agreed to hear from us by:
          </MDTypography>
          {CHANNELS.map((c) => (
            <Chip
              key={c.value}
              size="small"
              variant="outlined"
              color={consent[c.value] === true ? "success" : "default"}
              icon={
                <Icon sx={{ fontSize: "0.9rem !important" }}>
                  {consent[c.value] === true ? "check" : "block"}
                </Icon>
              }
              label={c.label}
            />
          ))}
        </MDBox>
        {refused.length > 0 && (
          <MDTypography variant="caption" color="warning" display="block" mb={2}>
            {contact.name} hasn&apos;t agreed to {refused.map((c) => LABELS[c]).join(" or ")} — that
            part won&apos;t be sent.
          </MDTypography>
        )}

        {channels.includes("email") && (
          <TextField
            label="Subject"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            sx={{ mb: 2 }}
            size="small"
          />
        )}
        <TextField
          label="Message"
          fullWidth
          multiline
          minRows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={`Write your message to ${contact.name}…`}
        />
        {(channels.includes("whatsapp") || channels.includes("sms")) && (
          <MDTypography variant="caption" color="text" display="block" mt={1}>
            An opt-out link is added to WhatsApp and SMS messages. If they haven&apos;t messaged you
            on WhatsApp in the last 24 hours, WhatsApp may hold back free-text messages.
          </MDTypography>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary" disabled={sending}>
          Close
        </MDButton>
        <MDButton
          onClick={handleSend}
          color="info"
          disabled={sending}
          startIcon={<Icon>send</Icon>}
        >
          {sending ? "Sending…" : `Send via ${channels.map((c) => LABELS[c]).join(" + ")}`}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

ContactMessageDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contact: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    communicationConsent: PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
    ),
  }).isRequired,
  send: PropTypes.func.isRequired,
};

export default ContactMessageDialog;
